import { authenticatedFetch } from "./apiClient";
import { parseApiErrorBody } from "../utils/parseApiError";
import { throwFromErrorBody } from "../utils/apiRequestError";

export type BroadcastStatus = "draft" | "scheduled" | "sending" | "sent" | "cancelled";

export interface BroadcastSegmentField {
  id: string;
  label: string;
  type: "string" | "boolean" | "datetime" | "number";
  operators: string[];
  values?: string[];
}

export interface NotificationSegmentDescriptor {
  fields: BroadcastSegmentField[];
}

/** Broadcast delivery/open stats from GET /api/admin/notification-broadcasts */
export interface NotificationBroadcastStats {
  delivered?: number;
  notifications_created?: number;
  total_targets?: number;
  finished_at?: string;
  opened_count?: number;
  open_rate?: number; // 0–100, one decimal
}

export interface NotificationBroadcast {
  id: string;
  title: string;
  body_markdown: string;
  category: string;
  target_segment: Record<string, unknown> | null;
  status: BroadcastStatus;
  scheduled_at: string | null;
  sent_at: string | null;
  stats?: NotificationBroadcastStats | Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CreateBroadcastPayload {
  title: string;
  body_markdown: string;
  category: string;
  target_segment: Record<string, unknown> | null;
  status: BroadcastStatus;
  scheduled_at?: string | null;
}

/** Normalize API error body to user message. */
function errorDetailMessage(body: unknown, fallback: string): string {
  return parseApiErrorBody(
    body && typeof body === "object" ? (body as Record<string, unknown>) : {},
    fallback
  ).message;
}

function throwAdminNotificationsError(status: number, body: unknown, fallback: string): never {
  throwFromErrorBody(
    status,
    body && typeof body === "object" ? body : {},
    errorDetailMessage(body, fallback) || fallback
  );
}

/** Parse error response body: backend may return JSON (envelope) or text/plain. */
async function parseErrorBody(response: Response): Promise<{ detail: unknown; fallback?: string }> {
  const contentType = response.headers.get("content-type") ?? "";
  const text = await response.text();
  const fallback = `Request failed: ${response.status} ${response.statusText}${text ? ` — ${text.slice(0, 200)}` : ""}`;
  if (contentType.includes("application/json") && text) {
    try {
      const body = JSON.parse(text);
      const parsed = parseApiErrorBody(body, fallback);
      return { detail: parsed.message, fallback };
    } catch {
      return { detail: text, fallback };
    }
  }
  return { detail: text || undefined, fallback };
}

export const fetchNotificationSegments = async (): Promise<NotificationSegmentDescriptor> => {
  const response = await authenticatedFetch("/admin/notification-segments");
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throwAdminNotificationsError(response.status, body, `Failed to fetch notification segments: ${response.status}`);
  }
  return (await response.json()) as NotificationSegmentDescriptor;
};

const DEBUG_NOTIFICATIONS = process.env.REACT_APP_DEBUG_NOTIFICATIONS === "true";

export const createNotificationBroadcast = async (
  payload: CreateBroadcastPayload
): Promise<NotificationBroadcast> => {
  if (DEBUG_NOTIFICATIONS) {
    console.log("[Notifications DEBUG] POST /admin/notification-broadcasts payload:", JSON.stringify(payload, null, 2));
  }
  const response = await authenticatedFetch("/admin/notification-broadcasts", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const responseText = await response.text();
  if (DEBUG_NOTIFICATIONS) {
    console.log("[Notifications DEBUG] POST response status:", response.status, "body:", responseText?.slice(0, 500));
  }

  if (!response.ok) {
    // Always log request and response on failure so backend/devs can diagnose 500s
    console.error("[Notifications] POST /admin/notification-broadcasts failed:", {
      status: response.status,
      statusText: response.statusText,
      requestPayload: payload,
      responseBody: responseText,
    });
    const err = responseText ? JSON.parse(responseText) : {};
    throwAdminNotificationsError(
      response.status,
      err,
      `${response.status} ${response.statusText}${responseText ? ` — ${responseText.slice(0, 200)}` : ""}`
    );
  }

  const data = responseText ? (JSON.parse(responseText) as NotificationBroadcast) : ({} as NotificationBroadcast);
  if (DEBUG_NOTIFICATIONS) {
    console.log("[Notifications DEBUG] POST success — status:", data.status, "stats:", data.stats, "id:", data.id);
  }
  return data;
};

export const updateNotificationBroadcast = async (
  id: string,
  payload: Partial<CreateBroadcastPayload>
): Promise<NotificationBroadcast> => {
  const response = await authenticatedFetch(`/admin/notification-broadcasts/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await parseErrorBody(response);
    throwAdminNotificationsError(
      response.status,
      body,
      body.fallback ?? `Failed to update notification broadcast: ${response.status}`
    );
  }

  return (await response.json()) as NotificationBroadcast;
};

export interface FetchBroadcastsParams {
  status?: BroadcastStatus | "all";
}

/**
 * Fetch admin notification broadcasts (list).
 */
export const fetchNotificationBroadcasts = async (
  params: FetchBroadcastsParams = {}
): Promise<NotificationBroadcast[]> => {
  const searchParams = new URLSearchParams();
  if (params.status && params.status !== "all") {
    searchParams.set("status", params.status);
  }
  const query = searchParams.toString();
  const url = query ? `/admin/notification-broadcasts?${query}` : "/admin/notification-broadcasts";
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throwAdminNotificationsError(response.status, body, `Failed to fetch notification broadcasts: ${response.status}`);
  }

  const json = await response.json();
  return Array.isArray(json) ? (json as NotificationBroadcast[]) : [];
};

/**
 * Cancel a scheduled broadcast.
 */
export const cancelNotificationBroadcast = async (id: string): Promise<NotificationBroadcast> => {
  const response = await authenticatedFetch(`/admin/notification-broadcasts/${id}/cancel`, {
    method: "POST",
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throwAdminNotificationsError(response.status, body, `Failed to cancel notification broadcast: ${response.status}`);
  }

  return (await response.json()) as NotificationBroadcast;
};
